import {Rectangle} from 'recharts';
import styled, {useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {PriceReportType} from '../../../../../types/PriceReportType';
import {CHART_SHAPE_OPACITY_TRANSITION} from '../consts/chartShapeOpacityTransition';
import {DIMMED_CHART_OPACITY} from '../consts/dimmedChartOpacity';
import {useChartToolbar} from '../hooks/useChartToolbar';
import {StatisticsChartItem} from '../types/StatisticsChartItem';
import {getBarHighlightPaths, hasBarTopHighlightEncodings} from '../utils/getBarHighlightPaths';
import {getChartHighlightBorderColor} from '../utils/getChartHighlightBorderColor';
import {getChartItemColor} from '../utils/getChartItemColor';
import {getChartPriceChangeDirection} from '../utils/getChartPriceChangeDirection';
import {isPrivateSeller} from '../utils/isPrivateSeller';
import {isVehicleInHighlightedGroup} from '../utils/isVehicleInHighlightedGroup';
import {MyVehicleShape} from './MyVehicleShape';

interface BarShapeProps {
  x?: number; // Passed by recharts
  y?: number; // Passed by recharts
  width?: number; // Passed by recharts
  height?: number; // Passed by recharts
  dataKey?: string; // Passed by recharts
  payload?: StatisticsChartItem; // Passed by recharts
  priceReport: PriceReportType;
  activeTooltipIndex: number | null;
  selectedVehicleIds: string[] | null;
}

export function BarShape(props: BarShapeProps) {
  const theme = useTheme();
  const {highlights, effectiveHighlightMode, selectedSellerId} = useChartToolbar();

  const isMyVehicle = props.payload?.car.isMyVehicle;
  const isMyAd = props.payload?.car.isMyAd;
  const isFocused = isNotNil(props.payload) && props.activeTooltipIndex === props.payload.index;
  const isSelected =
    isNotNil(props.payload) && !!props.selectedVehicleIds?.includes(props.payload.car.ad_id);

  const isLowerBar = props.dataKey === 'y1';
  const isHigherBar = props.dataKey === 'y2';
  const lowerBarHeight = props.payload?.y1 ?? 0;
  const higherBarHeight = props.payload?.y2 ?? 0;
  const totalBarHeight = props.payload?.y ?? 0;
  const isPositiveBar = totalBarHeight > lowerBarHeight;
  const isLowerBarShown = true;
  const isHigherBarShown = totalBarHeight > lowerBarHeight || isMyAd;
  const isThisBarShown = (isLowerBar && isLowerBarShown) || (isHigherBar && isHigherBarShown);
  const isLowerBarLight = false;
  const isHigherBarLight = isMyAd && !(isPositiveBar && (isFocused || isSelected));
  const isThisBarLight = (isHigherBar && isHigherBarLight) || (isLowerBar && isLowerBarLight);
  const hasLowerBarRadius = !isHigherBarShown || higherBarHeight === 0;
  const hasHigherBarRadius = !hasLowerBarRadius;
  const hasThisBarRadius = (isLowerBar && hasLowerBarRadius) || (isHigherBar && hasHigherBarRadius);

  if (!isThisBarShown) {
    return null;
  }

  if (isMyVehicle) {
    return <MyVehicleShape {...props} chartType="bar" />;
  }

  const isInHighlightedGroup = props.payload
    ? isVehicleInHighlightedGroup(
        props.payload.car,
        highlights,
        props.priceReport,
        selectedSellerId
      )
    : true;
  const isHidden = effectiveHighlightMode === 'hideOthers' && !isInHighlightedGroup;
  const isDimmed = effectiveHighlightMode === 'dimOthers' && !isInHighlightedGroup;

  // Keep hidden bars mounted at opacity 0 so mode changes can animate (same as scatter).
  const fillOpacity = match({isHidden, isDimmed})
    .with({isHidden: true}, () => 0)
    .with({isDimmed: true}, () => DIMMED_CHART_OPACITY)
    .otherwise(() => 1);
  const pointerEvents = isHidden ? 'none' : undefined;

  const color = props.payload
    ? getChartItemColor(
        theme,
        props.priceReport,
        props.payload?.car,
        isFocused,
        isSelected,
        isThisBarLight
      )
    : undefined;

  const priceChangeDirection =
    hasThisBarRadius && highlights.repriced && props.payload
      ? getChartPriceChangeDirection(props.payload.car, props.priceReport, true)
      : null;
  const showPrivateSellerHole =
    hasThisBarRadius &&
    highlights.sellerType &&
    isNotNil(props.payload) &&
    isPrivateSeller(props.payload.car);
  const borderColor =
    hasThisBarRadius && props.payload
      ? getChartHighlightBorderColor({
          theme,
          car: props.payload.car,
          highlights,
          selectedSellerId,
        })
      : null;
  const hasCap = isNotNil(borderColor);

  const shouldRenderHighlightShape =
    hasThisBarRadius &&
    hasBarTopHighlightEncodings({
      priceChangeDirection,
      showPrivateSellerHole,
      hasCap,
    });

  if (
    shouldRenderHighlightShape &&
    isNotNil(props.x) &&
    isNotNil(props.y) &&
    isNotNil(props.width) &&
    isNotNil(props.height)
  ) {
    const paths = getBarHighlightPaths({
      x: props.x,
      y: props.y,
      width: props.width,
      height: props.height,
      priceChangeDirection,
      showPrivateSellerHole,
      hasCap,
    });

    return (
      <g pointerEvents={pointerEvents}>
        <BarPath
          d={paths.bodyPath}
          fill={color}
          fillOpacity={fillOpacity}
          fillRule={paths.fillRule}
        />
        {isNotNil(paths.capPath) && !isNil(borderColor) ? (
          <BarPath d={paths.capPath} fill={borderColor} fillOpacity={fillOpacity} />
        ) : null}
      </g>
    );
  }

  return (
    <BarRectangle
      {...props}
      fill={color}
      fillOpacity={fillOpacity}
      radius={hasThisBarRadius ? [4, 4, 0, 0] : [0, 0, 0, 0]}
      pointerEvents={pointerEvents}
    />
  );
}

const BarPath = styled.path`
  transition: ${CHART_SHAPE_OPACITY_TRANSITION};
`;

const BarRectangle = styled(Rectangle)`
  transition: ${CHART_SHAPE_OPACITY_TRANSITION};
`;
